<script lang="ts">
	import { onMount } from 'svelte';
	import { decodeUserJWTV2 } from '$lib/utilites/jwt';
	import { Flags, OfferStatus, UserRole, type IOffer, type IUserJWT } from '../../../types/types';
	import { OffersService } from '$lib/services/offers.service';
	import userJwt from '$stores/user-jwt';
	import CustomerCompanyService from '$lib/services/customer_company.service';
	import Spinner from '../loading/Spinner.svelte';
	import Checkbox from '../checkbox/Checkbox.svelte';
	import tick from '$lib/assets/tick.svg';
	import InvalidOfferModal from '$lib/components/modals/Modal.svelte';
	import paymentRequired from '$lib/assets/paymentRequired.svg';

	const TERMS_AND_CONDITIONS = 'https://www.dock411.com/terms-and-conditions';
	const PRIVACY_POLICY = 'https://dock411.com/privacy-policy/';

	let offer: IOffer | null = null;
	let currentUser: IUserJWT | null = null;
	let loading = false;
	let loadingPayment = false;
	let loadingAgreeTerms = false;
	let displayModal = false;
	let agreeTerms = false;
	let validOffer = false;

	function sendEmail() {
		const email = 'sales@dock411.com';
		const subject = "Help! I'm having a problem with the proposal link you sent";
		const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}`;
		window.open(mailtoLink, '_blank');
	}

	async function acceptQuoteAndRedirectToStripe() {
		try {
			loadingPayment = true;
			const response = await OffersService.acceptQuote(offer!.stripe_quote_id);

			if (response.url) {
				window.location.href = response.url;
			}
		} catch (error) {
			validOffer = false;
		} finally {
			loadingPayment = false;
		}
	}

	async function fetchOffer() {
		try {
			loading = true;
			const response = await CustomerCompanyService.getOffer(currentUser!.cid);
			offer = response.data.offer;

			if (
				offer.customer_company_stripe_id &&
				offer.stripe_quote_id &&
				offer.status_code === OfferStatus.ready
			) {
				validOffer = true;
			} else {
				validOffer = false;
			}
		} catch (error) {
			validOffer = false;
		} finally {
			loading = false;
		}
	}

	async function acceptTermsAndConditions() {
		try {
			loadingAgreeTerms = true;
			await CustomerCompanyService.acceptTermsAndConditions(currentUser!.cid);
		} catch (error) {
			agreeTerms = false;
		} finally {
			loadingAgreeTerms = false;
		}
	}

	function getLabel(key: string | undefined) {
		if (!key) return;

		if (key === 'annual') return 'year';
		return key;
	}

	onMount(async () => {
		const unsubscribe = userJwt.subscribe(async (bearer) => {
			try {
				if (typeof bearer === 'object' && bearer && bearer.token) {
					currentUser = decodeUserJWTV2(bearer.token);

					if (currentUser!.flg && currentUser!.flg.includes(Flags.payment_required)) {
						displayModal = true;
					} else {
						displayModal = false;
					}
				}
			} catch (error) {}
		});

		if (displayModal) {
			await fetchOffer();
		}

		return unsubscribe;
	});
</script>

{#if !loading && currentUser && currentUser.flg.includes(Flags.payment_required)}
	{#if validOffer && currentUser}
		<div
			style="background-color: rgb(0, 0, 0, 0.2);"
			class="fixed z-[1000] top-0 left-0 w-full h-full {displayModal ? 'visible' : 'hidden'}"
		>
			<div
				class="w-[90%] sm:w-fit absolute left-1/2 top-[52%] transform -translate-x-1/2 -translate-y-1/2"
			>
				{#if currentUser?.rol?.includes(UserRole.customer_company_admin)}
					<div
						class="p-7 bg-[#FFF] flex flex-col items-center rounded-2xl outline-none border border-solid border-[#DDE2E5]"
					>
						<div class="rounded-[10px] w-full h-full">
							<p
								class="text-[20px] md:text-[28px] font-extrabold text-left text-[#FFF] rounded-t-lg p-3 md:p-6 bg-pos bg-gradient-to-r from-[#F89D1D] via-[#FFA800] to-[#F58520]"
							>
								Sign up for the Dock411 Facility 360º plan
							</p>
							<div
								class="border-2 border-solid border-[#DDE2E5] border-t-0 rounded-b-[10px] pb-8 bg-cover bg-center bg-[url('/gradient.jpg')]"
							>
								<div class="flex flex-col gap-5 p-6">
									<div class="flex items-center gap-4">
										<img src={tick} alt="" />
										<p class="md:text-xl text-[#2D3541] font-medium">Manage facility attributes</p>
									</div>
									<div class="flex items-center gap-4">
										<img src={tick} alt="" />
										<p class="md:text-xl text-[#2D3541] font-medium">Driver feedback hotline</p>
									</div>
									<div class="flex items-center gap-4">
										<img src={tick} alt="" />
										<p class="md:text-xl text-[#2D3541] font-medium">24/7 support</p>
									</div>
									<div class="flex items-center gap-4">
										<img src={tick} alt="" />
										<p class="md:text-xl text-[#2D3541] font-medium">Reputation monitoring</p>
									</div>
									<div class="flex items-center gap-4">
										<img src={tick} alt="" />
										<p class="md:text-xl text-[#2D3541] font-medium">Driver concierge services</p>
									</div>
								</div>
								<hr class="border-[1px] border-[#DDE2E5] w-[100%]" />
								<div class="flex items-center p-6 justify-center">
									<p
										style="box-shadow: 2px 2px 9px 2px rgba(0, 0, 0, 0.13);"
										class="px-2 md:text-[20px] bg-[#FFF] w-[90%] md:w-[80%] py-2.5 text-center"
									>
										${offer?.price ? parseFloat(offer?.price) / 100 : 0} per facility per {getLabel(
											offer?.preferred_payment_period_code
										)}
									</p>
								</div>
								<hr class="border-[1px] border-[#DDE2E5] w-[100%]" />
								<div class="flex flex-col items-center pt-6 gap-6 whitespace-nowrap">
									<div class="flex items-center gap-2 px-2">
										<span class="mt-[6px]">
											<Checkbox on:change={acceptTermsAndConditions} bind:checked={agreeTerms} />
										</span>
										<!-- svelte-ignore a11y-click-events-have-key-events -->
										<p class="text-[14px] whitespace-normal">
											I agree to the Dock411
											<span
												on:click={() => window.open(TERMS_AND_CONDITIONS)}
												class="underline underline-offset-2 cursor-pointer text-[#0062E0]"
												>Terms and Conditions
											</span>
											and
											<span
												on:click={() => window.open(PRIVACY_POLICY)}
												class="underline underline-offset-2 cursor-pointer text-[#0062E0]"
												>Privacy Policy</span
											>
										</p>
									</div>
									<button
										disabled={!agreeTerms || loadingPayment || loadingAgreeTerms}
										style="box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);"
										class="bg-[#F89D1D] mx-4 h-[54px] w-[268px] rounded-md text-[#FFF] disabled:opacity-50 flex"
										on:click={acceptQuoteAndRedirectToStripe}
									>
										{#if loadingPayment || loadingAgreeTerms}
											<div class="m-auto">
												<Spinner size="sm" />
											</div>
										{:else}
											<span class="text-xl font-extrabold m-auto">Pay Now</span>
										{/if}
									</button>
								</div>
							</div>
						</div>
					</div>
				{:else}
					<div
						class="bg-[#FFF] w-full sm:w-[534px] px-10 py-8 flex gap-6 flex-col items-center justify-center rounded-xl"
					>
						<img src={paymentRequired} alt="error" />
						<div class="flex flex-col gap-6 items-center text-center">
							<p class="text-2xl">Payment Required</p>
							<p class="text-xl">
								Payment is required before you can access this feature. Please contact your company
								administrator for more information.
							</p>
						</div>
					</div>
				{/if}
			</div>
		</div>
	{:else}
		<!-- This should be the invalid offer modal -->
		<InvalidOfferModal
			disableClickOutside={true}
			handleError={sendEmail}
			removeButton={currentUser.rol?.includes(UserRole.customer_company_user)}
		/>
	{/if}
{/if}
